import type { PartnershipsContent } from './index'

// Norsk bokmål.
//
// ⚠️ NEEDS ØYVIND'S PASS BEFORE THIS GOES TO PRODUCTION. The build brief and
// the copy file both say the Norwegian body should be *adapted* by Øyvind
// rather than translated, because the kommune register differs from the
// English one in more than vocabulary. This file is a faithful adaptation to
// edit down from, not a finished text — the argument and structure match the
// English pages so the two stay in step, but the phrasing wants a native ear,
// especially on the lokalsamfunn page, which a saksbehandler reads while
// building an internal case.
//
// Titles and meta descriptions are the brief's own (C7) and need no review.
// The arbeidsplasser description drops the brief's closing sentence
// ("Arbeidsgiver ser aldri familiens data") for the same reason the English
// one does: it is open item 1 and unconfirmed.
//
// Terminology follows lib/compare/data/no/: familiekalender, rutiner,
// handleliste, måltidsplanlegging, abonnement, offline. "Familieorganisering",
// never "familieplanlegging" — the latter means contraception and
// reproductive health, which is not what is on offer here.
const no: PartnershipsContent = {
  breadcrumbHub: 'For organisasjoner',

  hub: {
    meta: {
      title: 'Homie for organisasjoner | Partnerskap',
      description:
        'Ett rolig sted for familieuka, tilbudt gjennom din organisasjon. Gratis det første året for arbeidsgivere, kommuner og fagforeninger.',
    },
    h1: ['Hver person dere støtter', 'står i en ekstra vakt hjemme'],
    subhead: ['Et enkelt verktøy for å organisere familien,', 'tilbudt gjennom din organisasjon.'],
    heroCta: 'Bli partner',

    opening: {
      eyebrow: 'Ingen fører det opp på',
      highlight: 'EN TIMELISTE',
      paragraphs: [
        'Meldinger fra skolen, tannlegetimen, hvem sin tur det er til å ta med fotballtøyet, tingen som må kjøpes før torsdag. Det går i bakhodet hele dagen, hos én person, og det stopper ikke når arbeidsdagen begynner.',
        'Forskere kaller det mental belastning. De fleste organisasjoner ser det aldri, for utenfra ser det ikke ut som noe.',
        'Vi lagde Homie fordi vi sto i det selv. Ett delt sted for familieuka, slik at hele husstanden ser hva som skjer, ikke bare den forelderen som holder det sammen.',
      ],
    },

    fork: {
      eyebrow: 'To måter å gi Homie til dem',
      highlight: 'DERE HAR ANSVAR FOR',
      workplaces: {
        label: 'Arbeidsplasser',
        line: 'For dem dere har ansatt.',
        examples: 'Arbeidsgivere, HR-team, fagforeninger.',
      },
      communities: {
        label: 'Lokalsamfunn',
        line: 'For dem dere er til for.',
        examples: 'Kommuner, familiesentre, skoler, frivillige organisasjoner.',
      },
    },

    whatFamiliesGet: {
      eyebrow: 'Hva familiene',
      highlight: 'FAKTISK FÅR',
      paragraphs: [
        'Én delt kalender hele familien kan se og endre. Oppgaver som ikke krever at en forelder maser. Rutiner for morgen, skole og legging. Måltidsplanlegging, med oppskrifter som blir til en handleliste.',
        'Det virker på iOS og Android, offline like godt som på nett, på norsk og alle andre store språk. Det finnes en skjermmodus for et nettbrett på kjøkkenveggen, inkludert i alle abonnement.',
        'Ingen reklame. Ingen KI. Ingenting om en familie selges videre.',
      ],
    },

    startSmall: {
      eyebrow: 'En trygg måte å',
      highlight: 'STARTE I DET SMÅ',
      paragraphs: [
        'Partnerorganisasjoner får det første året gratis. Ingen lisenskostnad, ingen oppstartskostnad, ingen binding når året er omme.',
        'Vi gjør det slik fordi et år er lenge nok til å finne ut om familiene faktisk bruker det. Tre måneder er ikke det. Ut fra målene dere setter, ser vi hva som fungerer og hva som ikke gjør det for teamet deres, så dere kan bestemme hvordan partnerskapet skal fortsette.',
      ],
    },

    models: {
      eyebrow: 'Etter prøveåret,',
      highlight: 'TO MÅTER Å SAMARBEIDE',
      funded: {
        title: 'Finansiert tilgang',
        body: 'Organisasjonen deres betaler en årlig lisens, og familiene fortsetter å bruke Homie uten kostnad.',
      },
      official: {
        title: 'Offisiell partner',
        body: 'Ingen kostnad for organisasjonen. Familier som vil fortsette går over til et vanlig Homie-abonnement, og dere står oppført som partneren som ga dem det.',
      },
      closing:
        'De fleste arbeidsgivere lander på den første. De fleste organisasjoner uten en egen budsjettpost lander på den andre. Ingen av dem er standardvalget, og dere kan ombestemme dere når året er omme.',
    },

    story: {
      eyebrow: 'Historien',
      highlight: 'BAK DET',
      paragraphs: [
        'Homie ble laget av en norsk familie, for en norsk familie, før det ble laget for noen andre.',
        'En far som kaller seg selv strukturnerd, sannsynligvis en mestringsstrategi for ADHD-en. En kone som er husets strengeste kritiker og brukte en måned på å prøve det. En elleveåring som aldri husket gymbagen, og en mor som fungerte som menneskelig påminnelse, noe som var en dårlig løsning for dem begge.',
        'Vi ville ha noe som føles som en tavle på kjøleskapet, bare bedre. Det er fortsatt hele ideen.',
      ],
    },

    cta: {
      heading: 'Bli partner',
      blurb:
        'Fortell oss hvem dere har ansvar for, så sender vi en oversikt på én side. Ingen pitch-deck.',
      action: 'Kontakt oss',
    },
  },

  workplaces: {
    meta: {
      title: 'Familieorganisering som ansattgode | Homie',
      description: 'Gi de ansatte én delt familiekalender. Gratis det første året.',
    },
    h1: ['De ansatte har', 'en jobb nummer to hjemme.'],
    subhead: ['Ett rolig sted for familieuka,', 'tilbudt gjennom dere.'],
    heroCta: 'Bli partner',
    breadcrumbName: 'Arbeidsplasser',

    sections: [
      {
        eyebrow: 'Det dukker nesten aldri opp i',
        highlight: 'EN MEDARBEIDERUNDERSØKELSE',
        paragraphs: [
          'Det ser ut som at noen svarer litt tregere. Går presis klokka fem. Tar fri samme torsdag med noen ukers mellomrom uten å si hvorfor. Sier opp i mars uten en tydelig grunn.',
          'Under det meste av dette ligger en husstand som går på én persons hukommelse. Skjemaene, timene, utstyret, hentingen. Det er ekte arbeid og det er usynlig, og derfor dukker det nesten aldri opp i en medarbeiderundersøkelse.',
          'Dere kan ikke ta den belastningen fra noen, men å gjøre den synlig og delt med familien hjelper langt på vei. Det er der Homie kommer inn.',
        ],
      },
      {
        eyebrow: 'Hvem dette',
        highlight: 'ER FOR',
        paragraphs: [
          'Arbeidsgivere i alle størrelser. HR- og personalavdelinger. Fagforeninger som kjøper på vegne av medlemmer som ansatte.',
          'Det passer organisasjoner der en god del av de ansatte er foreldre eller pårørende, og der godebudsjettet er lite nok til at barnehagestøtte og fertilitetsdekning er utenfor rekkevidde.',
        ],
      },
      {
        eyebrow: 'Hvorfor dette ikke er',
        highlight: 'ENDA EN VELVÆREAPP',
        paragraphs: [
          'De fleste familiegoder er bygget rundt babyer. Barnehagestøtte, samarbeid med barnehager, fertilitetsdekning. Alt sammen faller bort når barnet begynner på skolen, og koordineringsarbeidet øker, det minker ikke.',
          'Homie brukes av hele husstanden, barna inkludert. En elleveåring kan sjekke hva hun trenger til i morgen uten å spørre noen. Det er den delen som gir en forelder kvelden tilbake.',
          'Og dere vil vite om det traff. Familier bruker enten en delt kalender eller så gjør de det ikke, og dere ser hva som skjedde før dere betaler noe.',
        ],
      },
      {
        eyebrow: 'Slik',
        highlight: 'FUNGERER DET',
        paragraphs: [
          'Dere tilbyr Homie til de ansatte.',
          'Familiene setter det opp selv, hjemme, i sitt eget tempo. Ingenting havner hos HR utover å fortelle folk at det finnes.',
        ],
      },
      {
        eyebrow: 'Hva det',
        highlight: 'KOSTER',
        paragraphs: [
          'Det første året er gratis. Ingen lisenskostnad, ingen oppstartskostnad, ingen binding når året er omme.',
          'Etter det går de fleste arbeidsgivere over til finansiert tilgang, der organisasjonen betaler en årlig lisens og familiene ikke betaler noe.',
        ],
        hubLink: {
          before: 'Prøveåret, de to partnermodellene og historien bak Homie står alt sammen på ',
          label: 'oversikten over partnerskap',
          after: '.',
        },
      },
    ],

    cta: {
      heading: 'Bli partner',
      blurb:
        'Fortell oss omtrent hvor mange familier dere har ansatt, så sender vi en oversikt på én side.',
      action: 'Kontakt oss',
    },

    crossLink: 'Ser du etter versjonen for dem dere er til for?',
  },

  communities: {
    meta: {
      title: 'Homie for kommuner og lokalsamfunn',
      description:
        'Gi familiene i kommunen ett rolig sted for uka. Gratis det første året, ingen kostnad for familiene. Start med ett område.',
    },
    h1: ['Familiene dere er til for', 'holder alt sammen i hodet.'],
    subhead: ['Ett rolig sted for uka,', 'tilbudt gjennom deres organisasjon.'],
    heroCta: 'Ta en prat',
    breadcrumbName: 'Lokalsamfunn',

    sections: [
      {
        eyebrow: 'Én person som holder',
        highlight: 'FOR MYE PÅ EN GANG',
        paragraphs: [
          'Hver familie går på informasjon som bor i hodet til én person. Hvor skjemaet er, når timen er, hvem som henter hvem.',
          'For de fleste familier er det slitsomt. For en familie som allerede står i mye, er det tingen som gjør alt annet vanskeligere. Bak en glemt time står det nesten alltid én person som holdt for mye på en gang, heller enn en familie som sluttet å bry seg.',
          'Ett delt sted for uka løser ikke det alene. Det tar bort én bestemt vekt, og det er en vekt som ligger tyngst på husstandene dere allerede jobber med.',
        ],
      },
      {
        eyebrow: 'Hvem dette',
        highlight: 'ER FOR',
        paragraphs: [
          'Kommuner og bydeler. Familiesentre og familiestøttetjenester. Skoler. Frivillige og ideelle organisasjoner som jobber med familier. Fagforeninger og foreninger som møter medlemmene sine som familier, ikke som ansatte.',
        ],
      },
      {
        eyebrow: 'Hva dere',
        highlight: 'KAN TILBY',
        paragraphs: [
          'Homie, gratis for familiene i kommunen, i ett år.',
          'Én delt kalender husstanden kan se og endre. Oppgaver og rutiner som ikke krever at en voksen står over dem. Måltidsplanlegging som blir til en handleliste.',
          'Det virker offline like godt som på nett, noe som betyr mer enn det høres ut som når data er noe en husstand budsjetterer med. Det finnes på norsk og alle andre store språk, så det treffer ikke bare familiene som allerede finner fram til tjenester.',
          'Ingen reklame. Ingen KI. Ingenting om en familie selges videre.',
        ],
      },
      {
        eyebrow: 'Start',
        highlight: 'I DET SMÅ',
        paragraphs: [
          'Dere trenger ikke rulle dette ut i hele kommunen for å finne ut om det virker.',
          'Velg én bydel, eller én tjeneste, eller én gruppe familier dere allerede kjenner godt. Kjør det i ett år. Velg ett resultat dere faktisk bryr dere om og mål akkurat det ordentlig, i stedet for å bygge et dashbord ingen leser.',
          'Har det ikke hjulpet når året er omme, har det ikke kostet dere noe, og da sier vi fra om det selv.',
        ],
      },
      {
        eyebrow: 'Hva det',
        highlight: 'KOSTER',
        paragraphs: [
          'Det første året er gratis. Ingen lisenskostnad, ingen oppstartskostnad, ingen binding.',
          'Etter det finnes det to måter å fortsette på. <b>Finansiert tilgang</b>, der organisasjonen deres betaler en årlig lisens og familiene fortsetter uten kostnad. Eller <b>offisiell partner</b>, der det ikke koster organisasjonen noe og familier som vil fortsette går over til et vanlig abonnement.',
          'Organisasjoner uten en egen budsjettpost velger som regel den andre. Det er et reelt valg, ikke en nødløsning.',
        ],
        hubLink: {
          before: 'Begge modellene, og historien bak Homie, står på ',
          label: 'oversikten over partnerskap',
          after: '.',
        },
      },
    ],

    cta: {
      heading: 'Ta en prat',
      blurb:
        'Fortell oss om ett område dere kunne tenke dere å starte med, så sender vi en oversikt på én side.',
      action: 'Kontakt oss',
    },

    crossLink: 'Ser du etter versjonen for dem dere har ansatt?',
  },
}

export default no
